import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, MapPin, Clock, ChatCircle, CheckCircle, Trash } from "@phosphor-icons/react";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    api.get("/jobs?mine=true").then(({ data }) => setJobs(data || [])).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const deleteJob = async (e, jobId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this job? This cannot be undone.")) return;
    try {
      await api.delete(`/jobs/${jobId}`);
      toast.success("Job deleted");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const statusBadge = (s) => {
    const map = {
      open: { label: "Open", cls: "bg-[rgba(226,109,41,0.12)] text-[var(--brand-orange)]" },
      in_progress: { label: "In progress", cls: "bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)]" },
      completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700" },
      cancelled: { label: "Cancelled", cls: "bg-gray-100 text-gray-600" },
    };
    const m = map[s] || map.open;
    return <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${m.cls}`}>{m.label}</span>;
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10" data-testid="customer-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }}>Welcome, {user?.name?.split(" ")[0] || "there"}</h1>
            <p className="text-[var(--text-muted)] mt-1">Your jobs and quotes, all in one place.</p>
          </div>
          <Link to="/post-job" className="btn-accent" data-testid="dashboard-post-job-btn"><Plus size={16} weight="bold" /> Post a job</Link>
        </div>

        <div className="mt-8">
          {loading ? <p className="text-[var(--text-muted)]">Loading…</p> :
            jobs.length === 0 ? (
              <div className="brand-card p-10 text-center" data-testid="customer-empty">
                <p className="text-[var(--text-muted)]">You haven't posted any jobs yet.</p>
                <Link to="/post-job" className="btn-primary mt-4 inline-flex" data-testid="customer-empty-cta">Post your first job</Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="customer-jobs-grid">
                {jobs.map((j) => (
                  <Link to={`/jobs/${j.job_id}`} key={j.job_id} className="brand-card p-5 relative" data-testid={`job-card-${j.job_id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <h3 className="font-display font-semibold text-lg text-[var(--brand-green)] line-clamp-1">{j.title}</h3>
                      {statusBadge(j.status)}
                    </div>
                    <p className="mt-1 text-sm text-[var(--text-muted)] line-clamp-2">{j.description}</p>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-[var(--text-muted)]">
                      <span className="inline-flex items-center gap-1"><MapPin size={12} /> {j.postcode}</span>
                      <span className="inline-flex items-center gap-1"><Clock size={12} /> {new Date(j.created_at).toLocaleDateString()}</span>
                      <span className="inline-flex items-center gap-1"><ChatCircle size={12} /> {j.quote_count} quote{j.quote_count===1?"":"s"}</span>
                    </div>
                    {(j.status === "open" || j.status === "cancelled") && (
                      <button
                        onClick={(e) => deleteJob(e, j.job_id)}
                        className="absolute top-3 right-3 p-1.5 rounded-md text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete job"
                        data-testid={`delete-job-${j.job_id}`}
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                  </Link>
                ))}
              </div>
            )
          }
        </div>
      </div>
    </Layout>
  );
}
